import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { getVariableAxes, AXIS_LABELS } from '@/lib/fonts';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// ─── Single axis slider ──────────────────────────────────────────────────────

function AxisSlider({ axisTag, range, value, onChange }) {
  const [min, max] = range;
  const label = AXIS_LABELS[axisTag] || axisTag.toUpperCase();

  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] w-16 shrink-0" style={{ color: 'var(--orbis-t3)' }}>
        {label}
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={axisTag === 'wght' ? 1 : axisTag === 'opsz' ? 1 : 0.1}
        value={value}
        onChange={e => onChange(axisTag, Number(e.target.value))}
        className="flex-1 h-1 rounded-full appearance-none cursor-pointer"
        style={{ accentColor: 'var(--orbis-blue)' }}
      />
      <span className="text-[10px] w-8 text-right tabular-nums shrink-0" style={{ color: 'var(--orbis-t2)' }}>
        {Math.round(value)}
      </span>
    </div>
  );
}

// ─── VariableFontAxesControl ────────────────────────────────────────────────

/**
 * Shows sliders for variable font axes only when the selected font supports them.
 * Hides itself entirely for non-variable fonts (Semplice principle: no empty states).
 *
 * Props:
 *   fontRole       — "heading" | "body"
 *   fontFamily     — current font family name
 *   currentAxes    — { wght: 700, opsz: 48 } from design_system.variable_font_axes
 *   projectId      — for the API call
 *   onApplied      — called with newMagicState after a successful apply
 */
export default function VariableFontAxesControl({
  fontRole,
  fontFamily,
  currentAxes,
  projectId,
  onApplied,
}) {
  const axes = getVariableAxes(fontFamily);

  // Build default values: use currentAxes if present, else midpoint of range
  function defaultForAxes(axesDef) {
    const result = {};
    for (const [tag, [min, max]] of Object.entries(axesDef)) {
      result[tag] = currentAxes?.[tag] ?? (tag === 'wght' ? Math.round((min + max) / 2) : min);
    }
    return result;
  }

  const [values, setValues] = useState(() => axes ? defaultForAxes(axes) : {});
  const [applying, setApplying] = useState(false);
  const debounceRef = useRef(null);

  // Re-sync when font family changes (axes switch)
  useEffect(() => {
    if (axes) setValues(defaultForAxes(axes));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fontFamily]);

  // Sync incoming currentAxes (e.g. after undo)
  useEffect(() => {
    if (axes && currentAxes) {
      setValues(prev => ({ ...prev, ...currentAxes }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAxes]);

  const sendPreview = useCallback((newValues) => {
    // Live preview: postMessage to iframe to inject a temporary <style>
    const iframes = document.querySelectorAll('iframe');
    if (!iframes.length) return;
    const variationCSS = Object.entries(newValues)
      .map(([tag, val]) => `"${tag}" ${val}`)
      .join(', ');
    const selector = fontRole === 'heading'
      ? 'h1, h2, h3, h4, h5, h6'
      : 'body, p, li, span';
    const css = `${selector} { font-variation-settings: ${variationCSS}; }`;
    iframes[0].contentWindow?.postMessage(
      { type: 'wf_preview_style', id: `orbis-var-font-${fontRole}`, css },
      '*'
    );
  }, [fontRole]);

  const handleChange = useCallback((tag, val) => {
    const next = { ...values, [tag]: val };
    setValues(next);
    // Debounced live preview (every 50ms while dragging)
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => sendPreview(next), 50);
  }, [values, sendPreview]);

  const handleApply = useCallback(async () => {
    if (!projectId || applying) return;
    setApplying(true);
    try {
      const resp = await axios.post(
        `${API_URL}/magic/projects/${projectId}/set-font-axes`,
        {
          font_role: fontRole,
          font_family: fontFamily,
          axes: values,
        }
      );
      toast.success('Font variation applied');
      if (onApplied) onApplied(resp.data?.magic_state);
    } catch (err) {
      toast.error('Failed to apply font variation');
    } finally {
      setApplying(false);
    }
  }, [projectId, fontRole, fontFamily, values, applying, onApplied]);

  // Don't render anything for non-variable fonts
  if (!axes) return null;

  return (
    <div
      className="mt-2 rounded-lg p-2 space-y-2"
      style={{ background: 'var(--orbis-s2)', border: '1px solid rgba(255,255,255,0.06)' }}
    >
      <p className="text-[9px] uppercase tracking-widest" style={{ color: 'var(--orbis-t4)' }}>
        Variable Axes
      </p>

      {Object.entries(axes).map(([tag, range]) => (
        <AxisSlider
          key={tag}
          axisTag={tag}
          range={range}
          value={values[tag] ?? range[0]}
          onChange={handleChange}
        />
      ))}

      <button
        onClick={handleApply}
        disabled={applying}
        className="w-full flex items-center justify-center gap-1.5 text-[10px] py-1 rounded-md transition-colors"
        style={{
          background: 'var(--orbis-blue)',
          color: 'var(--orbis-bg)',
          opacity: applying ? 0.6 : 1,
        }}
      >
        {applying
          ? <><Loader2 className="w-3 h-3 animate-spin" /> Applying...</>
          : 'Apply variation'}
      </button>
    </div>
  );
}
